import { lazy, Suspense, type ReactNode } from "react";

import { useT } from "../i18n/catalogue.js";
import { EmptyState } from "../states/index.js";
import { LoadingState } from "../states/index.js";
import { hasNegativeSlice, isEmptyDonut, type DonutSlice } from "./donut.js";

/**
 * A proportion of an exhaustive whole — the net worth composition, and the first mark in this package
 * that can REFUSE to draw what it is given.
 *
 * ==========================================================================================
 * IT OVERTURNS A REFUSAL THAT STILL STANDS, WHICH IS WHY THE ARGUMENT IS WRITTEN DOWN
 * ==========================================================================================
 *
 * `dashboard.md` refuses a pie or donut for the expense CATEGORIES and is right to: those rows are a
 * top ten of seventeen with no remainder, so no part-of-whole mark can be honest about them. It
 * states the rule that generalises — "a part-of-whole mark is honest only where the parts are
 * exhaustive" — and the net worth pillars satisfy it exactly. `donut.ts` carries the full argument.
 *
 * ==========================================================================================
 * THE REFUSAL IS IN THE PRIMITIVE, NOT IN THE CALLER
 * ==========================================================================================
 *
 * A negative part cannot be drawn as a share of a whole. The retired ring's entry says what the two
 * ways out are — erase the slice, or draw one larger than the circle — and both are lies.
 *
 * That check lives HERE rather than in the dashboard, so no future consumer can reach the shape
 * without it. `RankedBarChart` puts its scale in `ranked.ts` for the same reason: the one line that
 * separates an honest mark from a dishonest one should not be a thing each caller remembers.
 *
 * The caller supplies the SENTENCE, because only the caller knows what a negative part means in its
 * own subject — here, that the mortgage exceeds what the property is worth.
 *
 * `lazy` at MODULE SCOPE, as the other charts are, and both refusals are checked BEFORE the boundary
 * so a household in either state never downloads Recharts.
 */
const Impl = lazy(() => import("./DonutChartImpl.js").then((m) => ({ default: m.DonutChartImpl })));

export interface DonutChartProps {
  /** The parts, in the order they should be coloured. They must be EXHAUSTIVE of their sum. */
  slices: readonly DonutSlice[];
  /** Names the figure in the hole — "Net worth", not "Total". */
  totalLabel: string;
  /** Shown when there is nothing recorded yet. */
  emptyMessage?: ReactNode;
  /**
   * Shown INSTEAD OF THE CHART when a part is negative.
   *
   * Required, and deliberately not defaulted: a component that fell back to "no data" here would
   * report a household under water as a household with nothing recorded.
   */
  negativeMessage: ReactNode;
}

export function DonutChart({
  slices,
  totalLabel,
  emptyMessage,
  negativeMessage,
}: DonutChartProps): ReactNode {
  const t = useT();

  if (isEmptyDonut(slices)) return <EmptyState>{emptyMessage ?? t("ui.chart.empty")}</EmptyState>;
  if (hasNegativeSlice(slices)) return <EmptyState>{negativeMessage}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl slices={slices} totalLabel={totalLabel} />
    </Suspense>
  );
}
